import React, { useState, FC } from 'react'
import { BottomNavigation, BottomNavigationAction, Box, Menu, MenuItem, IconButton } from '@mui/material'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import GroupIcon from '@mui/icons-material/Group'
import ContactMailIcon from '@mui/icons-material/ContactMail'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ChairIcon from '@mui/icons-material/Chair'
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded'
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded'
import EventIcon from '@mui/icons-material/Event'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'

import { useTranslation } from 'react-i18next'
import { Network } from '@/core/models/network.model'
import { useNetworkMemberPermissions } from '../hooks/useNetworkMemberPermissions'

type NetworkDrawerProps = {
    network: Network
}

export const MobileNetworkDrawer: FC<NetworkDrawerProps> = (props) => {
    const { network } = props
    const { t } = useTranslation()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const location = useLocation()
    const navigate = useNavigate()

    const { isNetworkAdmin, isNetworkMember } = useNetworkMemberPermissions({
        networkId: network?._id,
        networkCreatorId: network?.createdBy._id,
        members: network?.members
    })

    // Determine which navigation item should be active based on the current path
    const getCurrentValue = () => {
        const path = location.pathname
        if (path.endsWith(`/networks/${network._id}`)) return 0
        if (path.includes('/members')) return 1
        if (path.includes('/consultation')) return 2
        if (path.includes('/expertise')) return 3
        return 0
    }

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const handleMenuItemClick = (path: string) => {
        navigate(path)
        handleMenuClose()
    }

    return (
        <>
            <Box sx={{ width: '100%', position: 'fixed', bottom: 0, backgroundColor: 'white', boxShadow: '0 -1px 5px rgba(0,0,0,0.1)', zIndex: 10 }}>
                <BottomNavigation
                    value={getCurrentValue()}
                    showLabels
                >
                    <BottomNavigationAction
                        id='mobile-network-home'
                        component={Link}
                        to={`/networks/${network._id}`}
                        label={t('page.network.mobileMenu.home')}
                        icon={<HomeRoundedIcon />}
                    />
                    <BottomNavigationAction
                        id='network-members'
                        component={Link}
                        to={`/networks/${network._id}/members`}
                        label={t('page.network.mobileMenu.members')}
                        icon={<GroupIcon />}
                    />
                    <BottomNavigationAction
                        id='network-consultation-requests'
                        component={Link}
                        to={`/networks/${network._id}/consultation`}
                        label={t('page.network.mobileMenu.consultation')}
                        icon={<ContactMailIcon />}
                    />
                    <BottomNavigationAction
                        id='network-expertise-requests'
                        component={Link}
                        to={`/networks/${network._id}/expertise`}
                        label={t('page.network.mobileMenu.expertise')}
                        icon={<VerifiedUserIcon />}
                    />
                    <BottomNavigationAction
                        id='network-more-menu'
                        onClick={handleMenuClick}
                        label={t('page.network.mobileMenu.more', 'More')}
                        icon={<MoreVertIcon />}
                    />
                </BottomNavigation>
                <Menu
                    id="mobile-network-more-menu"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                >
                    <MenuItem
                        onClick={() => handleMenuItemClick(`/networks/${network._id}/schedules`)}
                    >
                        <EventIcon sx={{ mr: 1 }} />
                        {t('page.network.tab.schedules.label')}
                    </MenuItem>

                    {isNetworkMember && (
                        <MenuItem
                            onClick={() => handleMenuItemClick(`/networks/${network._id}/plannings`)}
                        >
                            <CalendarMonthIcon sx={{ mr: 1 }} />
                            {t('page.network.menu.plannings', { defaultValue: 'Plannings' })}
                        </MenuItem>
                    )}

                    {isNetworkMember && (
                        <MenuItem
                            onClick={() => handleMenuItemClick(`/networks/${network._id}/waiting-rooms`)}
                        >
                            <ChairIcon sx={{ mr: 1 }} />
                            {t('page.network.menu.waitingRooms')}
                        </MenuItem>
                    )}

                    {isNetworkAdmin && (
                        <MenuItem
                            onClick={() => handleMenuItemClick(`/networks/${network._id}/member-requests`)}
                        >
                            <AdminPanelSettingsRoundedIcon sx={{ mr: 1 }} />
                            {t('page.network.menu.memberRequests')}
                        </MenuItem>
                    )}

                    {isNetworkAdmin && (
                        <MenuItem
                            onClick={() => handleMenuItemClick(`/networks/${network._id}/edit`)}
                        >
                            <SettingsSuggestRoundedIcon sx={{ mr: 1 }} />
                            {t('page.network.menu.settings')}
                        </MenuItem>
                    )}
                </Menu>
            </Box>
        </>
    )
}